<template>
  <Preloader v-if="loading" />
  <VRow v-else>
    <VCol cols="12">
      <VRow>
        <div class="templates-settings-description">
          <div class="templates-settings-description--text">
            {{ $t('editor.intro.1') }}
          </div>
          <div class="templates-settings-description--text">
            {{ $t('editor.intro.2') }}
          </div>
          <div class="templates-settings-description--text">
            {{ $t('editor.intro.3') }}
          </div>
        </div>
        <VCol cols="12">
          <VExpansionPanels flat hover focusable>
            <VExpansionPanel
              v-for="(group, groupIndex) in groupedNotifications"
              :key="groupIndex"
            >
              <VExpansionPanelHeader>
                <div class="notification-header-content">
                  <div @click.stop>
                    <Tooltip :position="'bottom-center'" :position-mob="'right'" :max-width="400">
                      <template #trigger>
                        <VSwitch
                          hide-details
                          inset
                          v-model="group[0].enabled"
                          :disabled="group.every(t => t.disabled)"
                          @change="setNotificationSettings(group, $event)"
                        />
                      </template>
                      <template #content>
                        <div>
                          {{ setTooltipText(group.every(t => t.disabled)) }}
                        </div>
                      </template>
                    </Tooltip>
                  </div>
                  <span class="switch-label">
                    {{ group[0].name }}
                  </span>
                </div>
              </VExpansionPanelHeader>
              <VExpansionPanelContent>
                <NotificationTemplate :group="group" />
              </VExpansionPanelContent>
            </VExpansionPanel>
          </VExpansionPanels>
        </VCol>
      </VRow>
    </VCol>
  </VRow>
</template>

<script>
import useNotificationTemplate from '@/compositions/useNotificationTemplate';
import useNotificationTemplateSettings from '@/compositions/useNotificationTemplateSettings';
import {
  onMounted, computed, toRefs, ref,
} from '@vue/composition-api';
import { useI18n } from 'vue-i18n-composable';
import NotificationTemplate from './NotificationTemplate.vue';
import ValidationMixin from '../../mixins/ValidationMixin';
import Preloader from '../Preloader.vue';
import Tooltip from '../common/Tooltip.vue';

export default {
  name: 'NotificationTemplatesList',
  components: { Preloader, NotificationTemplate, Tooltip },
  mixins: [ValidationMixin],
  props: {
    company: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const { company } = toRefs(props);
    const { t } = useI18n();

    function setTooltipText(isDisabled) {
      return isDisabled ? t('switchTooltipDisabled') : t('switchTooltip');
    }

    const {
      getNotificationTemplates,
      notificationTemplates,
    } = useNotificationTemplate();

    const {
      getNotificationTemplatesSettings,
      notificationTemplatesSettings,
      setNotificationTemplateSettings,
    } = useNotificationTemplateSettings();

    const loading = ref(false);
    const localeNotificationTemplates = ref([]);

    const groupedNotifications = computed(() => {
      if (!localeNotificationTemplates.value) return [];

      return Object.values(
        localeNotificationTemplates.value.reduce((acc, item) => {
          if (!acc[item.type]) acc[item.type] = [];
          acc[item.type].push(item);
          return acc;
        }, {}),
      );
    });

    async function setNotificationSettings(group, event) {
      if (!group || group.length === 0) return;

      const smsTemplate = group.find((t) => t.channel === 'sms');
      const emailTemplate = group.find((t) => t.channel === 'email');

      if (smsTemplate) {
        await setNotificationTemplateSettings({
          type: 'sms',
          notificationType: smsTemplate.type,
          enabled: event,
        });
      }

      if (emailTemplate) {
        await setNotificationTemplateSettings({
          type: 'email',
          notificationType: emailTemplate.type,
          enabled: event,
        });
      }

      group.forEach((t) => { t.enabled = event; });
    }

    onMounted(async () => {
      loading.value = true;
      await getNotificationTemplates(company.value.id);
      await getNotificationTemplatesSettings();

      const settingsMap = new Map();

      notificationTemplatesSettings.value.forEach((s) => {
        const key = `${s.notificationType}-${s.type}`;
        settingsMap.set(key, s.enabled);
      });

      const templates = [...notificationTemplates.value];
      templates.forEach((template) => {
        const key = `${template.type}-${template.channel}`;
        if (settingsMap.has(key)) {
          template.enabled = settingsMap.get(key);
        } else {
          template.enabled = true;
          template.disabled = true;
        }
      });

      localeNotificationTemplates.value = templates;
      loading.value = false;
    });

    return {
      groupedNotifications,
      setNotificationSettings,
      loading,
      setTooltipText,
    };
  },
};
</script>

<style lang="scss" scoped>
.notification-header-content {
  max-width: fit-content;
  display: flex;
  align-items: center;
}

.switch-label {
  font-size: 16px;
  line-height: 100%;
  color: rgba(33, 33, 33, 1);
  margin-left: 12px;
}
.templates-settings-description {
  margin-left: 10px;
  max-width: 56%;
  @media screen and (max-width: 768px) {
    max-width: 100%;
  }
}
.templates-settings-description--text {
  font-weight: 300;
  font-size: 16px;
  color: #212121;
}

::v-deep {
  .v-expansion-panel {
    border-bottom: 1px solid rgba(221, 221, 221, 1);
  }
  .v-expansion-panel--active:not(:first-child),
  .v-expansion-panel--active + .v-expansion-panel {
    margin-top: 0;
  }
  .v-expansion-panel-content__wrap {
    padding: 0 !important;
    margin: 40px 0;

    @media screen and (max-width: 1024px) {
      margin: 20px 0;
    }
  }
  .v-expansion-panel-header {
    padding: 22px 10px;
  }
}
</style>

<i18n locale="en" lang="yaml">
switchTooltip: You can activate or deactivate the function of sending a letter to the candidate.
switchTooltipDisabled: This mailing list is mandatory for sending to the candidate and cannot be disabled.
editor:
  intro:
    1: 'In this section, you can configure the automatic sending of emails and messages, as well as edit their content for candidates.'
    2: 'You will have the option to modify the message text and enable or disable sending.'
    3: |
      However, the sending of certain messages, such as a candidate response notification or an online interview invitation,
      is mandatory and cannot be disabled.
</i18n>

<i18n locale="ru" lang="yaml">
switchTooltip: Вы можете активировать или деактивировать функцию отправки письма кандидату
switchTooltipDisabled: Данное письмо рассылки являются обязательными для отправки кандидату и не подлежат отключению.
editor:
  intro:
    1: 'В данном разделе вы сможете настраивать автоматическую отправку писем и сообщений и редактировать их содержание для кандидатов.'
    2: 'У вас будет возможность изменить текст письма, а также активировать или деактивировать отправку.'
    3: |
      Однако отправка некоторых писем, таких как уведомление об отклике кандидата
      или приглашение на онлайн-интервью, является обязательной и не может быть отключена.
</i18n>
